import React, { useEffect, useState } from 'react';
import { Asset, Props, Resources } from './Assets.types';
import { Columns, Icon } from 'modules/af_cm_ui_kit';
import fields, { FIELDS, INPUTS } from 'modules/cm/fields/types';
import styles from './Assets.module.scss';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { useDispatch } from 'react-redux';
import { error, saveCase, setErrorModal } from 'modules/cm/store/cases/actions';
import validationSchema from './validationSchema';
import { Box, Button } from 'lmc-ui-library';
import useCaseIsReadOnly from 'helpers/hooks/useCaseIsReadOnly/useCaseIsReadOnly';
import usePepperError from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError';
import { FieldWithError } from 'modules/cm/pages/CaseDetailsPage/hooks/useFieldError/FieldWithError';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../form/yup/reactHookFormResolver';
import { assetsApi } from '../../../../../api';
import { updateApplicant } from '../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../hooks/useSaveButtonClicked';
import * as subPages from '../../../../../store/subPages';
import { SUBPAGES } from '../../../../../store/subPages/types';
import CheckDirty from '../../../../../components/CheckDirty/CheckDirty';

type AssetItemProps = {
  asset: Asset;
  onDelete: () => void;
  resources: Resources;
  modulePrefix: string;
  index: number;
  isCaseReadOnly: boolean;
} & FieldWithError;

type FormType = {
  [FIELDS.ASSETS_PREFIX]: Asset[];
};

const AssetItem = ({ modulePrefix, getErrors, resources, index, asset, isCaseReadOnly, onDelete }: AssetItemProps) => {
  if (!asset) {
    return <></>;
  }

  return (
    <div className={styles.item}>
      <div className={styles.row}>
        <Columns spacing={4}>
          <UpdatableField
            getErrors={getErrors}
            fieldType={INPUTS.SELECT}
            modulePrefix={modulePrefix}
            field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_TYPE]}
            options={resources.assetTypes}
            extraContent={[
              {
                position: 'bottom',
                variant: 'component',
                content: (
                  <UpdatableField
                    getErrors={getErrors}
                    fieldType={INPUTS.CHECKBOX}
                    modulePrefix={modulePrefix}
                    field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_WILL_BE_USED_TO_PAY_LOAN]}
                    options={resources.assetTypes}
                    variant="inline"
                  />
                ),
              },
            ]}
            required
          />

          <UpdatableField
            getErrors={getErrors}
            fieldType={INPUTS.TEXT}
            modulePrefix={modulePrefix}
            field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_VALUE]}
            variant={VARIANTS_TYPES.CURRENCY}
          />

          <UpdatableField
            getErrors={getErrors}
            fieldType={INPUTS.TEXTAREA}
            modulePrefix={modulePrefix}
            field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_DESCRIPTION]}
          />
        </Columns>
        <div className={styles.actions}>
          <Button
            mode="ghost"
            borderVariant="rectangle"
            onClick={() => onDelete()}
            type="button"
            disabled={isCaseReadOnly}>
            <Icon type="trash" />
          </Button>
        </div>
      </div>
      {asset.type === 'PROPERTY' && (
        <>
          <div className={styles.row}>
            <Columns spacing={4}>
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_OUTSTANDING_MORTGAGE_BALANCE]}
                variant={VARIANTS_TYPES.CURRENCY}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_PROPERTY_POSTCODE]}
                variant={VARIANTS_TYPES.POSTCODE}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_PROPERTY_CITY]}
              />
            </Columns>
          </div>
          <div className={styles.row}>
            <Columns spacing={4}>
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_PROPERTY_FLAT_NUMBER]}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_PROPERTY_BUILDING_NAME]}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_PROPERTY_BUILDING_NUMBER]}
              />
              <UpdatableField
                getErrors={getErrors}
                fieldType={INPUTS.TEXT}
                modulePrefix={modulePrefix}
                field={fields[FIELDS.ASSETS_PREFIX](index)[FIELDS.ASSETS_PROPERTY_STREET]}
              />
            </Columns>
          </div>
        </>
      )}
    </div>
  );
};

const Assets: React.FC<Props> = ({
  modulePrefix,
  caseId,
  data,
  initialData,
  resources,
  applicantId,
  applicantIndex,
}) => {
  const dispatch = useDispatch();
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);
  const initialValues = { [FIELDS.ASSETS_PREFIX]: initialData };
  const isCaseReadOnly = useCaseIsReadOnly();

  const methods = useForm<FormType>({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { control, reset } = methods;

  useEffect(() => {
    reset(
      { [FIELDS.ASSETS_PREFIX]: data },
      {
        keepDefaultValues: currentApplicantIndex === applicantIndex,
      },
    );
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [data, applicantIndex, currentApplicantIndex, reset]);

  const { fields: assets, append, remove } = useFieldArray({
    control,
    keyName: 'key',
    name: FIELDS.ASSETS_PREFIX,
  });

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  const addItem = () => {
    assetsApi(caseId)(applicantId)
      .postAsset()
      .then((res) => {
        append(res.data);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
        dispatch(error(err));
      });
  };

  const deleteAsset = (assetId: number, index: number) => {
    assetsApi(caseId)(applicantId)
      .deleteAsset(assetId)
      .then(() => {
        remove(index);
      })
      .catch((err) => {
        dispatch(setErrorModal(err));
      });
  };

  const { getErrors } = usePepperError('applicants', applicantIndex);

  return (
    <Box title="Assets">
      <FormProvider<FormType> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(applicantIndex, SUBPAGES.APPLICANT_ASSETS, isDirty),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <>
            <Box.Content>
              {assets.map((field, index: number) => (
                <AssetItem
                  key={field.key}
                  modulePrefix={modulePrefix}
                  resources={resources}
                  asset={methods.getValues()[FIELDS.ASSETS_PREFIX][index]}
                  onDelete={() => deleteAsset(((field as any) as Asset).id, index)}
                  getErrors={getErrors}
                  index={index}
                  isCaseReadOnly={isCaseReadOnly}
                />
              ))}
            </Box.Content>
            <Box.Footer>
              <Button mode="ghost" borderVariant="rectangle" onClick={addItem} type="button" disabled={isCaseReadOnly}>
                Add another asset
              </Button>
            </Box.Footer>
          </>
        </form>
      </FormProvider>
    </Box>
  );
};

export default Assets;
